import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import SummaryCards from "../../../DesignSystem/SummaryCards/SummaryCards";
import MonthlyChargeBreakdown from "./MonthlyChargeBreakdown";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import { useAdviserCharges } from "../context/AdviserChargesContext";
import { chargesForMonth, formatMonthKey } from "../utils/chargeDates";
import { isMonthlyFeeApplicable } from "../utils/monthlyFeeGuideline";
import { monthlyFeeForComparison } from "../../../Adviser/Tabs/Rates/rateHistory";
import { getIncomePeriodRatePolicy } from "../utils/incomePeriod";
import { loadAMLArchiveOnce } from "../../AMLStatements.js/amlDashboard";
import { ReactComponent as ArrowIncreaseIcon } from "../../../../assets/icons/paraplanningDashboard/ArrowIncrease.svg";
import { ReactComponent as ArrowDecreaseIcon } from "../../../../assets/icons/paraplanningDashboard/ArrowDecrease.svg";

const groups = [
  { type: "Monthly Fees", label: "Monthly Fees", tone: "cream" },
  { type: "Paraplanning", label: "Paraplanning Charges", tone: "blue" },
  { type: "AML Checks", label: "AML Checks", tone: "purple", count: true },
  {
    type: "Website & Graphics",
    label: "Website & Graphics Charges",
    tone: "green",
  },
  {
    type: "Miscellaneous",
    label: "Miscellaneous Charges",
    tone: "pink",
  },
];
const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    value,
  );
const total = (records, group) =>
  records
    .filter((row) => row.chargeType === group.type)
    .reduce(
      (sum, row) =>
        sum +
        (group.count
          ? 1
          : Math.round(Number(row.amountCharged || 0) * 100) / 100),
      0,
    );

const MonthlyChargesInsights = () => {
  const {
    selectedMonth,
    monthOptions,
    chargeRecords,
    loadedChargeRecords,
    sources,
    incomePeriods,
    chargesLoading,
    chargeErrors,
  } = useAdviserCharges();
  const navigate = useNavigate();
  const [year, month] = selectedMonth.split("-").map(Number);
  const previousMonth =
    month === 1
      ? `${year - 1}-12`
      : `${year}-${String(month - 1).padStart(2, "0")}`;
  const monthLabel = formatMonthKey(selectedMonth);
  const previousAvailable = monthOptions.some(
    (option) => option.value === previousMonth,
  );
  const [previousAmlRecords, setPreviousAmlRecords] = useState([]);
  const [previousAmlLoading, setPreviousAmlLoading] = useState(false);
  const [previousAmlError, setPreviousAmlError] = useState("");
  useEffect(() => {
    let active = true;
    setPreviousAmlRecords([]);
    setPreviousAmlError("");
    if (!previousAvailable) {
      setPreviousAmlLoading(false);
      return () => { active = false; };
    }
    setPreviousAmlLoading(true);
    loadAMLArchiveOnce(previousMonth).then(
      (records) => {
        if (!active) return;
        setPreviousAmlRecords(records);
        setPreviousAmlLoading(false);
      },
      () => {
        if (!active) return;
        setPreviousAmlRecords([]);
        setPreviousAmlError("Previous AML checks could not be loaded.");
        setPreviousAmlLoading(false);
      },
    );
    return () => { active = false; };
  }, [previousAvailable, previousMonth]);
  const previousRatePolicy = useMemo(
    () => getIncomePeriodRatePolicy(incomePeriods || [], previousMonth),
    [incomePeriods, previousMonth],
  );
  const previousRecords = useMemo(() => {
    const records = chargesForMonth(
      loadedChargeRecords.filter(
        (row) => !["Monthly Fees", "AML Checks"].includes(row.chargeType),
      ),
      previousMonth,
    );
    records.push(...previousAmlRecords);
    (sources["Monthly Fees"] || []).forEach((row) => {
      if (!isMonthlyFeeApplicable(row, previousMonth)) return;
      const amount = monthlyFeeForComparison(
        row.rateData,
        row.history,
        previousMonth,
        previousRatePolicy,
      );
      if (amount !== null && amount !== "" && amount !== undefined)
        records.push({
          chargeType: "Monthly Fees",
          amountCharged: Number(amount),
        });
    });
    return records;
  }, [loadedChargeRecords, previousAmlRecords, sources, previousMonth, previousRatePolicy]);
  const cards = groups.map((group) => {
    const value = total(chargeRecords, group);
    const difference =
      Math.round((value - total(previousRecords, group)) * 100) / 100;
    const comparable =
      (group.type === "Monthly Fees"
        ? Array.isArray(sources["Monthly Fees"])
        : group.type === "AML Checks"
          ? previousAvailable && !previousAmlLoading && !previousAmlError
          : previousAvailable) &&
      !(
        group.count &&
        previousMonth === monthOptions[0].value &&
        !sources["AML archive"]
      );
    const description = chargeErrors.filter(Boolean).length
      ? "Some charge data is unavailable"
      : group.type === "AML Checks" && previousAmlLoading
        ? "Loading previous month…"
      : !comparable
        ? "Previous month unavailable"
        : difference === 0
          ? "No change from last month"
          : group.count
            ? `${Math.abs(difference)} ${difference > 0 ? "more" : "fewer"} checks than last month`
            : `${money(Math.abs(difference))} ${difference > 0 ? "increase" : "decrease"} from last month`;
    return {
      id: group.type,
      tone: group.tone,
      label: (
        <span className="space-between-row gap-s">
          <span>{group.label}</span>
          <span>{monthLabel}</span>
        </span>
      ),
      value: chargesLoading
        ? "-"
        : group.count
          ? value.toLocaleString("en-GB")
          : money(value),
      description: chargesLoading ? (
        "Loading…"
      ) : (
        <span className={`monthly-insights-change ${comparable && !chargeErrors.filter(Boolean).length ? difference < 0 ? "monthly-insights-change--down" : "monthly-insights-change--up" : ""}`}>
          {comparable &&
            difference !== 0 &&
            !chargeErrors.filter(Boolean).length && (
              <span
                aria-hidden="true"
                
              >
                {difference > 0 ? <ArrowIncreaseIcon width={12} height={12}/> : <ArrowDecreaseIcon width={12} height={12}/>}
              </span>
            )}
          {description}
        </span>
      ),
    };
  });
  return (
    <section
      className="fit-column gap-m width-100"
      aria-label="Monthly Charges Insights"
    >
      <div className="space-between-row gap-m">
        <h4 className="FP_Subtitle_Semibold text-black">
          Monthly Charges Insights
        </h4>
        <PrimaryButton
          type="button"
          label="Charges Breakdown"
          onClick={() =>
            navigate(`${APP_ROUTES.CHARGES_BREAKDOWN}?month=${selectedMonth}`)
          }
        />
      </div>
      <SummaryCards cards={cards} grid="5" loading={chargesLoading} />
      <MonthlyChargeBreakdown records={chargeRecords} monthLabel={monthLabel} loading={chargesLoading} />
    </section>
  );
};
export default MonthlyChargesInsights;
